<script lang='ts'>
export default {
  name: 'CreateQuoteProgramModal',
};
</script>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { Plus } from 'lucide-vue-next';
import ConfirmModal from '@/components/ConfirmModal.vue';
import AppButton from '@/components/v2/AppButton.vue';
import Checkbox from '@/components/v2/Checkbox.vue';
import TextField from '@/components/v2/TextField.vue';
import Combobox from '@/components/v2/Combobox.vue';
import type { ComboboxOption } from '@/components/v2/Combobox.vue';
import DatePickerInput from '@/components/v2/DatePickerInput.vue';
import { quoteService } from '@/services/api';
import { NewBusiness, Renewal } from '@/utilities/ProgramTypes';
import { QuoteProgram } from '@/types/Quote';
import { ComboboxItem } from '@/types/Vuetify';
import { useRoute } from 'vue-router';

const props = defineProps<{
  coverageTypes: ComboboxItem[];
}>();

const route = useRoute();
const emit = defineEmits(['needs-update', 'show-snackbar']);

const progName = ref('');
const year = ref('');
const selectedProgramType = ref('');
const renewalDate = ref('');
const coverageType = ref<ComboboxItem | null>(null);
const keepOpenSelected = ref(false);
const renewal = ref('Renewal');

const programName = computed(() => {
  if (progName.value.trim() === '' && coverageType.value && year.value.trim() !== '') {
    return `${ year.value } - ${ coverageType.value.title }`;
  }
  return progName.value;
});

const renewalSelected = computed(() => {
  return selectedProgramType.value === renewal.value;
});

const coverageTypeOptions = computed<ComboboxOption[]>(() =>
  props.coverageTypes.map((c) => ({ label: c.title, value: String(c.value) })),
);
const programTypeOptions: ComboboxOption[] = [
  { label: 'Renewal', value: 'Renewal' },
  { label: 'New Business', value: 'New Business' },
];

const isValid = computed(() => {
  if (
    programName.value.trim() === '' ||
    coverageType.value?.title.trim() === '' || !coverageType.value ||
    year.value.trim() === '' ||
    selectedProgramType.value.trim() === ''
  ) {
    return false;
  }
  return !(selectedProgramType.value === renewal.value && (renewalDate.value + '').trim() === '');
});

function setCoverageTypeFromValue(value: string) {
  const found = props.coverageTypes.find((c) => String(c.value) === value);
  coverageType.value = found ?? null;
}

function onYearInput(value: string | number) {
  year.value = String(value).replace(/[A-z]/g, '');
}

async function createProgram(): Promise<boolean> {
  //submit
  if (!coverageType.value) {
    return true;
  }

  // Convert date string (YYYY-MM-DD) to ISO datetime format for backend
  const expirationDate = renewalDate.value && renewalDate.value !== '' 
    ? new Date(renewalDate.value + 'T00:00:00').toISOString()
    : undefined;

  const quote: QuoteProgram = {
    accountID: parseInt(route.params.id as string),
    coverageTypeId: coverageType.value.value,
    coverageType: coverageType.value.title,
    year: parseInt(year.value),
    programType:
      selectedProgramType.value === renewal.value ? Renewal : NewBusiness,
    currentProgramExpiration: expirationDate,
    programName: programName.value.trim()
  };

  await quoteService.CreateQuoteProgram(quote);
  emit('needs-update', true);
  emit('show-snackbar', true);
  clearForm();
  return true;
}

function clearForm(): void {
  progName.value = '';
  year.value = '';
  selectedProgramType.value = '';
  renewalDate.value = '';
  coverageType.value = null;
}

</script>

 <template>
  <ConfirmModal
    title="Create Program"
    :action="createProgram"
    confirmText="Create"
    size="md"
    :dismiss="clearForm"
    :isConfirmDisabled="() => !isValid"
    :keepOpen="keepOpenSelected"
  >
    <template v-slot:button="{ props: activatorProps }">
      <AppButton
        primary
        class="tw:ml-3"
        data-testid="createquote-btn"
        v-bind="activatorProps"
      >
        Create Quote Program
        <Plus :size="16" class="tw:ml-1" />
      </AppButton>
    </template>

    <template #expandedActions>
      <div class="tw:flex tw:items-center tw:mr-3">
        <Checkbox
          v-model="keepOpenSelected"
          label="Create Another"
          data-test="keepopen-checkbox"
        />
      </div>
    </template>

    <div class="tw:flex tw:flex-col tw:gap-4 tw:p-3">
      <TextField
        :model-value="programName"
        label="Program Name"
        data-testid="quote-program-name"
        @update:model-value="(v: string | number) => (progName = String(v))"
      />
      <div class="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-4">
        <div class="tw:form-control tw:w-full">
          <Combobox
            label="Coverage Type"
            :model-value="coverageType ? String(coverageType.value) : ''"
            :options="coverageTypeOptions"
            placeholder="Select coverage type"
            data-testid="quote-coveragetype-cbo"
            @update:model-value="setCoverageTypeFromValue"
          />
        </div>
        <TextField
          :model-value="year"
          label="Year"
          data-testid="quote-year-text"
          @update:model-value="onYearInput"
        />
      </div>
      <div class="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-4">
        <div class="tw:form-control tw:w-full">
          <Combobox
            label="Program Type"
            v-model="selectedProgramType"
            :options="programTypeOptions"
            placeholder="Select program type"
            data-testid="quote-programType-cbo"
          />
        </div>
        <DatePickerInput
          v-if="renewalSelected"
          v-model="renewalDate"
          outlined
          label="Renewal Date"
          data-testid="quote-renewal-date-picker"
        />
      </div>
    </div>
  </ConfirmModal>
</template>

